import {
  Button,
  Card,
  CardContent,
  CardHeader,
  H5,
  H6,
  InputImage,
  InputImageTile,
  InputTextArea,
  InputCheckbox,
  InputNumber,
} from "@sellar-io/components";
import type {
  ProductCreateRequestTypeProductType,
  SupplierProductGetResponseType,
  ProductImageType,
} from "@sellar-io/api-v2/types";
import { ReactElement } from "react";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import { useForm } from "react-hook-form";
import { useRemoteFeatureFlags } from "../../../../hooks/useRemoteFeatureFlags";
import { useFirebaseImageUpload } from "../../../../hooks/useFirebaseImageUpload";
import sellarLogoBg from "../../../../images/sellar-logo-bg.png";
import sellarBadge from "../../../../images/sellar-badge.svg";

export default function ProductItemFormV3({
  product,
  onSubmit,
  onDelete,
}: {
  product?: SupplierProductGetResponseType;
  onSubmit: (product: ProductCreateRequestTypeProductType) => void;
  onDelete?: () => void;
  loading?: boolean;
}): ReactElement {
  const uploadImage = useFirebaseImageUpload();
  const { register, handleSubmit, getValues, setValue } =
    useForm<ProductCreateRequestTypeProductType>({
      defaultValues: {
        name: product?.name ?? undefined,
        kind: product?.kind ?? undefined,
        abv: product?.abv ?? undefined,
        description: product?.description ?? undefined,
        imageUrl: product?.imageUrl ?? undefined,
        heroImageUrl: product?.heroImageUrl ?? undefined,
        vegan: product?.vegan ?? undefined,
        glutenFree: product?.glutenFree ?? undefined,
        lactoseFree: product?.lactoseFree ?? undefined,
        new: product?.new ?? undefined,
        type: "product",
        ProductImages:
          product?.ProductImages && product.ProductImages.length > 0
            ? product.ProductImages
            : [
                {
                  url: product?.heroImageUrl ?? "",
                  position: 1,
                },
              ],
      },
      shouldUseNativeValidation: true,
    });

  const getImage = (position: ProductImageType["position"]) => {
    return (
      getValues("ProductImages")?.filter(
        (image) => image.position === position,
      )[0]?.url ?? null
    );
  };

  const updateImage = (image: ProductImageType) => {
    const currentImages = getValues("ProductImages") ?? [];
    const imagesMinusPosition = currentImages.filter(
      (cImage) => cImage.position != image.position,
    );
    imagesMinusPosition.push(image);
    setValue("ProductImages", imagesMinusPosition);
  };

  return (
    <div className={"flex flex-col gap-4"}>
      <Card className="bg-white">
        <CardHeader className="bg-white p-4">
          <H5>Product badge</H5>
          <H6>
            Upload an image with a 1:1 ratio, Minimum width 200 pixels. Maximum
            file size 5MB.
          </H6>
        </CardHeader>
        <CardContent>
          <InputImage
            value={getValues("imageUrl") ?? undefined}
            uploadFile={uploadImage}
            onChange={(url) => setValue("imageUrl", url)}
          />
        </CardContent>
      </Card>
      <Card className="bg-white">
        <CardHeader className="bg-white p-4">
          <H5>Hero images</H5>
          <H6>
            Upload up to 4 images that best showcase your product. The first
            image will be used as the primary hero image. Recommended ratio
            16:9. Minimum width 600 pixels. Maximum file size 5MB.
          </H6>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid w-full grid-cols-2 gap-2 sm:grid-cols-4">
            {[...Array(4).keys()].map((index) => (
              <InputImageTile
                key={index}
                value={getImage(index + 1)}
                uploadFile={uploadImage}
                onChange={(url) =>
                  updateImage({
                    url,
                    position: index + 1,
                  })
                }
                className="w-full"
              />
            ))}
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white">
        <CardHeader className="min-h-0 bg-white p-4">
          <H5>Key info</H5>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className={"flex w-full flex-col gap-2 sm:flex-row"}>
            <div className="flex w-full flex-col gap-2 sm:w-1/3">
              <InputLabel>*Name</InputLabel>
              <InputContainer>
                <InputText
                  {...register("name", { required: true })}
                  placeholder="JP Finest"
                />
              </InputContainer>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-1/3">
              <InputLabel>Kind</InputLabel>
              <InputContainer>
                <InputText
                  {...register("kind", { required: false })}
                  placeholder="Pale Ale"
                />
              </InputContainer>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-1/3">
              <InputLabel>ABV %</InputLabel>
              <InputContainer>
                <InputNumber
                  {...register("abv", { required: false, valueAsNumber: true })}
                  placeholder="3.5%"
                  step={0.1}
                  min={0}
                />
              </InputContainer>
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <InputLabel>Description</InputLabel>
            <InputContainer>
              <InputTextArea
                variant="legacyFixed"
                {...register("description", { required: false })}
                className="h-[100px]"
              />
            </InputContainer>
          </div>
        </CardContent>
      </Card>
      <Card className="bg-white">
        <CardHeader className="bg-white p-4">
          <H5>Dietary</H5>
          <H6>Declare dietary information that applies to this product</H6>
        </CardHeader>
        <CardContent className="flex flex-col gap-4 p-4">
          <div className={"flex w-full flex-col gap-2 sm:flex-row"}>
            <div className="flex w-full flex-col gap-2 sm:w-1/3">
              <InputContainer className="border-none">
                <InputCheckbox {...register("vegan", { required: false })} />
                <InputLabel>Vegan</InputLabel>
              </InputContainer>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-1/3">
              <InputContainer className="border-none">
                <InputCheckbox
                  {...register("glutenFree", { required: false })}
                />
                <InputLabel>Gluten free</InputLabel>
              </InputContainer>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-1/3">
              <InputContainer className="border-none">
                <InputCheckbox
                  {...register("lactoseFree", { required: false })}
                />
                <InputLabel>Lactose free</InputLabel>
              </InputContainer>
            </div>
          </div>
        </CardContent>
      </Card>
      <PreorderMarketingCard />
      <div className={"sticky bottom-0 mt-2 flex gap-2 bg-zinc-100 py-4"}>
        <Button className={"w-full"} onClick={handleSubmit(onSubmit)}>
          {product?.id ? "Save" : "Create"}
        </Button>
        {onDelete && (
          <Button color={"red"} className={"w-full"} onClick={onDelete}>
            Delete
          </Button>
        )}
      </div>
    </div>
  );
}

const PreorderMarketingCard = () => {
  const remoteFeatureFlag = useRemoteFeatureFlags();
  const linkUrl =
    "https://intercom.help/sellario/en/articles/10214249-supplier-guide-pre-orders";
  return (
    <div
      className="rounded-lg border border-zinc-300 bg-[length:50px_50px] bg-repeat p-3 font-sans"
      style={{
        backgroundImage: `url(${sellarLogoBg})`,
      }}
    >
      <div className="mb-4 flex flex-col gap-2">
        <img className={"h-[48px]"} src={sellarBadge} />
        <div className="flex items-center justify-center text-zinc-800">
          <span className="text-xl font-bold">Setting up a pre-order?</span>
        </div>
        <div className="text-center text-base">
          Attract customers • Generate Hype • Boost your brand
        </div>
      </div>

      <div className="flex items-center justify-center">
        <Button
          color="secondary"
          onClick={() => window.open(linkUrl, "_blank")}
        >
          {remoteFeatureFlag.isActive("preOrders")
            ? "Get started"
            : "Learn more"}
        </Button>
      </div>
    </div>
  );
};
